// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Code generated by Microsoft (R) AutoRest Code Generator.
// Changes may cause incorrect behavior and will be lost if the code is regenerated.

import type { PagedAsyncIterableIterator } from "@azure/core-paging";
import type { SimplePollerLike, OperationState } from "@azure/core-lro";
import type {
  FleetspaceResource,
  FleetspaceListOptionalParams,
  FleetspaceGetOptionalParams,
  FleetspaceGetResponse,
  FleetspaceCreateOptionalParams,
  FleetspaceCreateResponse,
  FleetspaceUpdateOptionalParams,
  FleetspaceUpdateResponse,
  FleetspaceDeleteOptionalParams,
  FleetspaceDeleteResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Interface representing a Fleetspace. */
export interface Fleetspace {
  /**
   * Lists all the fleetspaces under a fleet.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param options The options parameters.
   */
  list(
    resourceGroupName: string,
    fleetName: string,
    options?: FleetspaceListOptionalParams,
  ): PagedAsyncIterableIterator<FleetspaceResource>;
  /**
   * Retrieves the properties of an existing Azure Cosmos DB fleetspace under a fleet
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param fleetspaceName Cosmos DB fleetspace name. Needs to be unique under a fleet.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    fleetName: string,
    fleetspaceName: string,
    options?: FleetspaceGetOptionalParams,
  ): Promise<FleetspaceGetResponse>;
  /**
   * Creates an Azure Cosmos DB fleetspace under a fleet.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param fleetspaceName Cosmos DB fleetspace name. Needs to be unique under a fleet.
   * @param body The parameters to provide for the current fleetspace.
   * @param options The options parameters.
   */
  beginCreate(
    resourceGroupName: string,
    fleetName: string,
    fleetspaceName: string,
    body: FleetspaceResource,
    options?: FleetspaceCreateOptionalParams,
  ): Promise<SimplePollerLike<OperationState<FleetspaceCreateResponse>, FleetspaceCreateResponse>>;
  /**
   * Creates an Azure Cosmos DB fleetspace under a fleet.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param fleetspaceName Cosmos DB fleetspace name. Needs to be unique under a fleet.
   * @param body The parameters to provide for the current fleetspace.
   * @param options The options parameters.
   */
  beginCreateAndWait(
    resourceGroupName: string,
    fleetName: string,
    fleetspaceName: string,
    body: FleetspaceResource,
    options?: FleetspaceCreateOptionalParams,
  ): Promise<FleetspaceCreateResponse>;
  /**
   * Update the properties of an existing Azure Cosmos DB fleetspace under a fleet.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param fleetspaceName Cosmos DB fleetspace name. Needs to be unique under a fleet.
   * @param options The options parameters.
   */
  beginUpdate(
    resourceGroupName: string,
    fleetName: string,
    fleetspaceName: string,
    options?: FleetspaceUpdateOptionalParams,
  ): Promise<SimplePollerLike<OperationState<FleetspaceUpdateResponse>, FleetspaceUpdateResponse>>;
  /**
   * Update the properties of an existing Azure Cosmos DB fleetspace under a fleet.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param fleetspaceName Cosmos DB fleetspace name. Needs to be unique under a fleet.
   * @param options The options parameters.
   */
  beginUpdateAndWait(
    resourceGroupName: string,
    fleetName: string,
    fleetspaceName: string,
    options?: FleetspaceUpdateOptionalParams,
  ): Promise<FleetspaceUpdateResponse>;
  /**
   * Deletes an existing Azure Cosmos DB Fleetspace.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param fleetspaceName Cosmos DB fleetspace name. Needs to be unique under a fleet.
   * @param options The options parameters.
   */
  beginDelete(
    resourceGroupName: string,
    fleetName: string,
    fleetspaceName: string,
    options?: FleetspaceDeleteOptionalParams,
  ): Promise<SimplePollerLike<OperationState<FleetspaceDeleteResponse>, FleetspaceDeleteResponse>>;
  /**
   * Deletes an existing Azure Cosmos DB Fleetspace.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param fleetspaceName Cosmos DB fleetspace name. Needs to be unique under a fleet.
   * @param options The options parameters.
   */
  beginDeleteAndWait(
    resourceGroupName: string,
    fleetName: string,
    fleetspaceName: string,
    options?: FleetspaceDeleteOptionalParams,
  ): Promise<FleetspaceDeleteResponse>;
}
